import Vue from 'vue'
import {
  RESET_STATE,
  SET_BYPASS,
  REMOVE_BYPASS,
  REMOVE_BYPASSES,
  SET_BYPASS_CLEAR_REQUEST,
  REMOVE_BYPASS_CLEAR_REQUEST,
  REMOVE_BYPASS_CLEAR_REQUESTS,
  SET_BYPASS_NOW,
  SET_BYPASS_SETTINGS,
  SET_BYPASS_SEEDED,
  SET_BYPASS_TICKER,
  SET_BYPASS_INFO,
  SET_BYPASS_CREATE,
  SET_SECURITY_ACTION_ERROR,
} from '@/store/mutation-types'
import { describeActionError } from '@/models/securityActionError'
import { markApprovalResolved, isApprovalResolved } from '@/models/resolvedApprovals'
import {
  mapCameraBypass,
  mapBypassApprovalRequest,
  bypassDurationPayload,
  bypassMaxFixedSec,
  parseServerTime,
} from '@/models/bypass'
import apiBypass from '@/apis/siteBypass'
import apiApproval from '@/apis/siteApproval'

// Zone-aware fixed-duration ceiling for a camera (24-hour zones cap shorter).
// Reads the parsed device's zone type; falls back to the default (24h) cap.
function maxFixedSecFor (rootGetters, deviceId) {
  const dev = rootGetters.siteDevice ? rootGetters.siteDevice(deviceId) : null
  const zoneType = dev && dev.zoneInfo ? dev.zoneInfo.zoneType : null
  return bypassMaxFixedSec(zoneType)
}

// Most-restrictive ceiling across several cameras (one duration applied to all).
function maxFixedSecForMany (rootGetters, deviceIds) {
  const caps = (deviceIds || []).map(function (id) { return maxFixedSecFor(rootGetters, id) })
  return caps.length ? Math.min.apply(null, caps) : undefined
}

// ─────────────────────────────────────────────────────────────────────────────
// Camera Bypass store (MOCK).
// There is no backend for bypass yet, so this module owns an in-memory model:
//   - a map of deviceId -> bypass record
//   - a global "now" ticker so countdowns re-render every second
//   - auto-expiry of fixed-duration bypasses (Spec cross-feature rule #6)
// Actions mimic async API calls (resolve after a short delay) so the UI wiring
// matches what a real BE integration will look like.
// ─────────────────────────────────────────────────────────────────────────────

// Module-scoped ticker handle (only one interval, ever).
let tickerHandle = null

// Clock-skew correction (ms): server_time − client Date.now(), captured from the
// list response. Countdowns/expiry compare server-time expires_at against a
// server-aligned "now" (Date.now() + serverOffset) so a wrong client clock can't
// expire/extend a bypass early or late. Stays 0 in mock mode (no fetch, all
// timestamps are client-local). (backend guidance 2026-08-21)
let serverOffset = 0

// Apply a BypassActionResult body (create/edit/clear) to the store. The body
// always carries camera_bypass AND approval_request with one of them null.
// sid = the site the action targeted; stamped onto records so site-scoped
// getters can see them (#1).
function commitBypassBody (commit, body, sid) {
  if (!body) return
  if (body.camera_bypass) {
    const rec = mapCameraBypass(body.camera_bypass, sid)
    if (rec.status === 'ended') commit(REMOVE_BYPASS, { deviceId: rec.deviceId })
    else commit(SET_BYPASS, { deviceId: rec.deviceId, record: rec })
  } else if (body.approval_request) {
    const rec = mapBypassApprovalRequest(body.approval_request, sid)
    if (rec.deviceId == null) return
    if (rec.action === 'clear_bypass') commit(SET_BYPASS_CLEAR_REQUEST, { deviceId: rec.deviceId, record: rec })
    else commit(SET_BYPASS, { deviceId: rec.deviceId, record: rec })
  }
}

// Default per-type Approval Settings (Spec §9). Every approval type — bypass,
// temporary disarm, arm, disarm — owns an independent copy of these.
function defaultApprovalSettings () {
  return {
    // Site Owner "immediately" toggles: pre-authorize monitoring-center actions
    // so they apply with no approval prompt. Off by default.
    ownerImmediately: false,
    ownerClearImmediately: false,
    // Non-owner Request Timeout Behavior (2-min window): 'allow' | 'refuse'.
    // The action and its clear/cancel counterpart are configured independently
    // (default refuse).
    nonOwnerTimeoutBehavior: 'refuse',
    nonOwnerClearTimeoutBehavior: 'refuse',
  }
}

const initialState = {
  bypasses: {}, // deviceId -> record
  // deviceId -> pending clear_bypass approval request. Separate from `bypasses`
  // because the same device still holds its active bypass record while a clear
  // request is pending its approval.
  clearRequests: {},
  // Approval Settings keyed by type (bypass | temporaryDisarm | arm | disarm).
  settings: {
    bypass: defaultApprovalSettings(),
    temporaryDisarm: defaultApprovalSettings(),
    arm: defaultApprovalSettings(),
    disarm: defaultApprovalSettings(),
  },
  now: Date.now(),
  seededSiteId: null,
  // Global single-camera bypass-info dialog (rendered app-wide from App.vue).
  infoOpen: false,
  infoDeviceId: null,
  // Global single-camera bypass-create dialog (rendered app-wide from App.vue).
  createOpen: false,
  createCtx: null, // { deviceId, siteId, deviceName, preArm }
  // Last failed user-initiated security action (bypass / temp-disarm / arming /
  // approval), shown app-wide by SecurityActionErrorDialog. Shared with the
  // tempDisarm module — modules aren't namespaced, so it commits the same type.
  actionError: null, // { title, message, detail } | null
}

const state = Vue.util.extend({}, initialState)

// Surface a failed user-initiated action in the app-wide error dialog, then
// re-throw so callers can still react (e.g. re-enable a submit button).
// Only user actions use this — background fetches/resyncs stay silent.
function failAction (commit, err) {
  commit(SET_SECURITY_ACTION_ERROR, describeActionError(err))
  throw err
}

const getters = {
  bypassRecord: function (state) {
    return function (deviceId) {
      return state.bypasses[deviceId] || null
    }
  },
  // A camera is "bypassed" only once active — pending (awaiting) requests don't count.
  isCameraBypassed: function (state) {
    return function (deviceId) {
      const r = state.bypasses[deviceId]
      return !!r && r.status !== 'awaiting'
    }
  },
  // All bypass records for the CURRENTLY SELECTED site. Bypass state is per-site,
  // so every list/count below is scoped to selectedSiteId (records carry siteId).
  bypassRecords: function (state, getters, rootState, rootGetters) {
    const sid = rootGetters.selectedSiteId
    return Object.values(state.bypasses).filter(r => r.siteId === sid)
  },
  // Pending dealer/operator-initiated requests awaiting end-user approval.
  bypassRequests: function (state, getters, rootState, rootGetters) {
    const sid = rootGetters.selectedSiteId
    return Object.values(state.bypasses).filter(r => r.status === 'awaiting' && r.siteId === sid)
  },
  bypassedDeviceIds: function (state, getters, rootState, rootGetters) {
    const sid = rootGetters.selectedSiteId
    return Object.values(state.bypasses).filter(r => r.siteId === sid).map(r => r.deviceId)
  },
  // Number of cameras currently bypassed (active states only) on the selected site.
  bypassCount: function (state, getters, rootState, rootGetters) {
    const sid = rootGetters.selectedSiteId
    return Object.values(state.bypasses).filter(r => r.status === 'bypassed' && r.siteId === sid).length
  },
  // Awaiting operator-initiated approval requests on the selected site.
  bypassAwaitingCount: function (state, getters, rootState, rootGetters) {
    const sid = rootGetters.selectedSiteId
    return Object.values(state.bypasses).filter(r => r.status === 'awaiting' && r.siteId === sid).length
  },
  // Pending clear_bypass requests (MC asks to clear an active bypass) for the site.
  bypassClearRequests: function (state, getters, rootState, rootGetters) {
    const sid = rootGetters.selectedSiteId
    return Object.values(state.clearRequests).filter(r => r.siteId === sid)
  },
  bypassClearAwaitingCount: function (state, getters, rootState, rootGetters) {
    const sid = rootGetters.selectedSiteId
    return Object.values(state.clearRequests).filter(r => r.siteId === sid).length
  },
  // Approval Settings for a given type (bypass | temporaryDisarm | arm | disarm).
  approvalSettings: function (state) {
    return function (type) {
      return state.settings[type] || null
    }
  },
  bypassNow: function (state) {
    return state.now
  },
  bypassInfoOpen: function (state) {
    return state.infoOpen
  },
  bypassInfoDeviceId: function (state) {
    return state.infoDeviceId
  },
  bypassCreateOpen: function (state) {
    return state.createOpen
  },
  bypassCreateCtx: function (state) {
    return state.createCtx
  },
  // Last failed security action, for the app-wide error dialog.
  securityActionError: function (state) {
    return state.actionError
  },
}

const actions = {
  dismissSecurityActionError: function ({ commit }) {
    commit(SET_SECURITY_ACTION_ERROR, null)
  },

  // An approval_request.resolved arrived on MQTT. Remember the id (for ANY
  // action) so an eventually-consistent pending list can't resurrect it.
  noteApprovalResolved: function (ctx, { approvalRequest } = {}) {
    const ar = approvalRequest || {}
    markApprovalResolved(ar.id, parseServerTime(ar.expires_at))
  },
  // Start the countdown ticker and load the live list. Safe to call repeatedly
  // (on mount / site change).
  initBypass: function ({ commit, dispatch }, { siteId } = {}) {
    startTicker(commit)
    return dispatch('fetchBypasses', { siteId })
  },

  // Fetch the live active-bypass + pending-request list for a site (real API).
  // Replaces this site's records with the server truth (E-API-11/12: re-sync on
  // MQTT reconnect / app resume by re-dispatching this).
  fetchBypasses: function ({ commit, state, rootGetters }, { siteId } = {}) {
    const sid = siteId || rootGetters.selectedSiteId
    startTicker(commit)
    return apiBypass.listCameraBypasses({ siteId: sid }).then(res => {
      const body = (res && res.body) || {}
      const st = parseServerTime(body.server_time)
      if (st != null) serverOffset = st - Date.now()
      const serverNow = Date.now() + serverOffset
      const stale = Object.keys(state.bypasses).filter(id => String(state.bypasses[id].siteId) === String(sid))
      if (stale.length) commit(REMOVE_BYPASSES, { deviceIds: stale })
      const staleClear = Object.keys(state.clearRequests).filter(id => String(state.clearRequests[id].siteId) === String(sid))
      if (staleClear.length) commit(REMOVE_BYPASS_CLEAR_REQUESTS, { deviceIds: staleClear })
      const cameraBypasses = body.camera_bypasses || []
      cameraBypasses.forEach(cb => {
        const rec = mapCameraBypass(cb, sid)
        if (rec.deviceId == null) return
        commit(SET_BYPASS, { deviceId: rec.deviceId, record: rec })
      })
      // Route pending approval requests by action. bypass requests live in the
      // `bypasses` map (keyed by device); clear_bypass requests live in the
      // separate `clearRequests` map (the device still holds its active bypass).
      // Actions without a device_id (temp_disarm/arm/disarm) belong elsewhere and
      // are skipped by the deviceId guard.
      ;(body.approval_requests || []).forEach(ar => {
        // Skip requests whose 2-min window already elapsed — the backend may still
        // list them as pending before it resolves them to expired/rejected.
        const exp = parseServerTime(ar.expires_at)
        if (exp != null && exp <= serverNow) return
        // Already resolved locally / elsewhere — the list just hasn't caught up.
        // Re-adding it would pop the approval modal for a finished request.
        if (isApprovalResolved(ar.id, serverNow)) return
        const rec = mapBypassApprovalRequest(ar, sid)
        if (rec.deviceId == null) return
        if (rec.action === 'clear_bypass') commit(SET_BYPASS_CLEAR_REQUEST, { deviceId: rec.deviceId, record: rec })
        else if (rec.action === 'bypass') commit(SET_BYPASS, { deviceId: rec.deviceId, record: rec })
      })
      commit(SET_BYPASS_SEEDED, sid)
    }).catch(err => {
      // List re-fetch is a best-effort re-sync (nudge). A 4xx/5xx (e.g. endpoint
      // not yet deployed) must not surface as an unhandled rejection — log & move on.
      Vue.logger ? Vue.logger.warn('fetchBypasses failed: ' + (err && err.status)) : console.warn('fetchBypasses failed', err && err.status)
    })
  },

  // End-user approves a pending (operator-initiated) request.
  approveBypassRequest: function ({ commit, state, rootGetters }, { deviceId }) {
    const r = state.bypasses[deviceId]
    if (!r || r.status !== 'awaiting' || !r.approvalRequestId) return Promise.resolve()
    const sid = r.siteId || rootGetters.selectedSiteId
    return apiApproval.approveApprovalRequest({ siteId: sid, requestId: r.approvalRequestId }).then(res => {
      markApprovalResolved(r.approvalRequestId, r.requestExpiresAt)
      const body = (res && res.body) || {}
      if (body.camera_bypass) {
        const rec = mapCameraBypass(body.camera_bypass, sid)
        commit(SET_BYPASS, { deviceId: rec.deviceId, record: rec })
      } else {
        commit(REMOVE_BYPASS, { deviceId })
      }
    }).catch(err => failAction(commit, err))
  },

  // End-user rejects a pending request.
  rejectBypassRequest: function ({ commit, state, rootGetters }, { deviceId }) {
    const r = state.bypasses[deviceId]
    if (!r || !r.approvalRequestId) {
      commit(REMOVE_BYPASS, { deviceId })
      return Promise.resolve()
    }
    const sid = r.siteId || rootGetters.selectedSiteId
    return apiApproval.declineApprovalRequest({ siteId: sid, requestId: r.approvalRequestId })
      .then(() => {
        markApprovalResolved(r.approvalRequestId, r.requestExpiresAt)
        commit(REMOVE_BYPASS, { deviceId })
      })
      .catch(err => failAction(commit, err))
  },

  // End-user approves a pending clear_bypass request → the active bypass ends.
  approveBypassClearRequest: function ({ commit, state, rootGetters }, { deviceId }) {
    const r = state.clearRequests[deviceId]
    if (!r || !r.approvalRequestId) return Promise.resolve()
    const sid = r.siteId || rootGetters.selectedSiteId
    return apiApproval.approveApprovalRequest({ siteId: sid, requestId: r.approvalRequestId }).then(() => {
      // Clear approved → drop the request and the now-ended active bypass. MQTT /
      // the next fetch reconciles authoritatively either way.
      markApprovalResolved(r.approvalRequestId, r.requestExpiresAt)
      commit(REMOVE_BYPASS_CLEAR_REQUEST, { deviceId })
      commit(REMOVE_BYPASS, { deviceId })
    }).catch(err => failAction(commit, err))
  },

  // End-user rejects a pending clear_bypass request → the active bypass stays.
  rejectBypassClearRequest: function ({ commit, state, rootGetters }, { deviceId }) {
    const r = state.clearRequests[deviceId]
    if (!r || !r.approvalRequestId) {
      commit(REMOVE_BYPASS_CLEAR_REQUEST, { deviceId })
      return Promise.resolve()
    }
    const sid = r.siteId || rootGetters.selectedSiteId
    return apiApproval.declineApprovalRequest({ siteId: sid, requestId: r.approvalRequestId })
      .then(() => {
        markApprovalResolved(r.approvalRequestId, r.requestExpiresAt)
        commit(REMOVE_BYPASS_CLEAR_REQUEST, { deviceId })
      })
      .catch(err => failAction(commit, err))
  },

  // Bypass a single camera (end-user direct action = applied immediately, PRD §7.2).
  // Pre-arm (PRD §7.4) is different: made while DISARMED it is a LOCAL queue — the
  // create endpoint 409s while disarmed — that converts to an active until-next-disarm
  // bypass on the next arm (convertPreArmForPartitions). So pre-arm never hits the API.
  bypassCamera: function ({ commit, rootGetters }, { deviceId, siteId, durationKey, customMs, reason, preArm }) {
    startTicker(commit)
    const sid = siteId || rootGetters.selectedSiteId
    if (preArm) {
      // Pre-arm (PRD §7.4): local queue while disarmed (create 409s when disarmed);
      // converts to an active until-next-disarm bypass on the next arm.
      commit(SET_BYPASS, { deviceId, record: buildRecord(deviceId, sid, {
        status: 'pre-arm', mode: 'preArm', startTime: Date.now(), endTime: null,
        durationKey, durationLabel: 'Until Next Disarm',
        initiator: 'me', initiatorName: 'You', reason: reason || '',
      }) })
      return Promise.resolve()
    }
    const payload = bypassDurationPayload(durationKey, customMs, maxFixedSecFor(rootGetters, deviceId))
    return apiBypass.createCameraBypass({ siteId: sid, deviceId, ...payload })
      .then(res => commitBypassBody(commit, res && res.body, sid))
      .catch(err => failAction(commit, err))
  },

  // Bypass multiple cameras with a single duration applied to all (PRD §7.3).
  bypassCameras: function ({ commit, rootGetters }, { deviceIds, siteId, durationKey, customMs, preArm }) {
    startTicker(commit)
    const sid = siteId || rootGetters.selectedSiteId
    const ids = deviceIds || []
    if (preArm) {
      // Pre-arm queue (see bypassCamera) — local only, no API.
      ids.forEach(deviceId => commit(SET_BYPASS, {
        deviceId,
        record: buildRecord(deviceId, sid, {
          status: 'pre-arm', mode: 'preArm', startTime: Date.now(), endTime: null,
          durationKey, durationLabel: 'Until Next Disarm', initiator: 'me', initiatorName: 'You',
        }),
      }))
      return Promise.resolve()
    }
    const payload = bypassDurationPayload(durationKey, customMs, maxFixedSecForMany(rootGetters, ids))
    return Promise.all(ids.map(deviceId =>
      apiBypass.createCameraBypass({ siteId: sid, deviceId, ...payload })
        .then(res => commitBypassBody(commit, res && res.body, sid))
    )).catch(err => failAction(commit, err))
  },

  // PRD §7.4/§13: arming a partition converts its queued pre-arm selections into
  // active "Until Next Disarm" bypasses. Dispatched from the arm flow (partition store).
  convertPreArmForPartitions: function ({ commit, state, rootGetters }, { siteId, partitionIds } = {}) {
    const sid = siteId || rootGetters.selectedSiteId
    const pset = new Set((partitionIds || []).map(String))
    const camPart = {}
    ;(rootGetters.cameraList || []).forEach(c => { camPart[String(c.deviceId)] = String(c.sitePartitionId) })
    const inScope = r => r.status === 'pre-arm' && String(r.siteId) === String(sid) &&
      (pset.size === 0 || pset.has(camPart[String(r.deviceId)]))
    const targets = Object.values(state.bypasses).filter(inScope)
    if (!targets.length) return Promise.resolve()
    return Promise.all(targets.map(r =>
      apiBypass.createCameraBypass({ siteId: sid, deviceId: r.deviceId, untilNextDisarm: true })
        .then(res => commitBypassBody(commit, res && res.body, sid))
        .catch(() => {})
    ))
  },

  // PRD §7.4/§13: pre-arm selections are cleared if their partition is disarmed
  // before being armed. Local-only (queued pre-arm never reached the server).
  clearPreArmForPartitions: function ({ commit, state, rootGetters }, { siteId, partitionIds } = {}) {
    const sid = siteId || rootGetters.selectedSiteId
    const pset = new Set((partitionIds || []).map(String))
    const camPart = {}
    ;(rootGetters.cameraList || []).forEach(c => { camPart[String(c.deviceId)] = String(c.sitePartitionId) })
    const ids = Object.keys(state.bypasses).filter(id => {
      const r = state.bypasses[id]
      return r.status === 'pre-arm' && String(r.siteId) === String(sid) &&
        (pset.size === 0 || pset.has(camPart[String(r.deviceId)]))
    })
    if (ids.length) commit(REMOVE_BYPASSES, { deviceIds: ids })
    return Promise.resolve()
  },

  // Edit (extend/shorten) an active bypass. Spec §9-4: the returned camera_bypass.id
  // is NEW; UI keys by device_id.
  editBypass: function ({ commit, state, rootGetters }, { deviceId, durationKey, customMs, preArm }) {
    const r = state.bypasses[deviceId]
    const sid = (r && r.siteId) || rootGetters.selectedSiteId
    const payload = preArm ? { untilNextDisarm: true } : bypassDurationPayload(durationKey, customMs, maxFixedSecFor(rootGetters, deviceId))
    const expectedBypassId = r && r.id ? r.id : undefined
    return apiBypass.editCameraBypass({ siteId: sid, deviceId, ...payload, expectedBypassId })
      .then(res => commitBypassBody(commit, res && res.body, sid))
      .catch(err => failAction(commit, err))
  },

  // Clear a single camera bypass (immediate, Spec §4 "즉시 clear").
  clearBypass: function ({ commit, state, rootGetters }, { deviceId }) {
    const r = state.bypasses[deviceId]
    const sid = (r && r.siteId) || rootGetters.selectedSiteId
    const expectedBypassId = r && r.id ? r.id : undefined
    return apiBypass.clearCameraBypass({ siteId: sid, deviceId, expectedBypassId })
      .then(() => commit(REMOVE_BYPASS, { deviceId }))
      .catch(err => failAction(commit, err))
  },

  // Clear all active bypasses (leaves pending approval requests untouched).
  clearAllBypasses: function ({ state, rootGetters, dispatch }) {
    const sid = rootGetters.selectedSiteId
    const ids = Object.keys(state.bypasses).filter(id =>
      state.bypasses[id].status !== 'awaiting' && state.bypasses[id].siteId === sid)
    return Promise.all(ids.map(deviceId => dispatch('clearBypass', { deviceId })))
  },

  // Approval Settings persist locally only (no backend API yet).
  updateApprovalSettings: function ({ commit }, { type, patch }) {
    commit(SET_BYPASS_SETTINGS, { type, patch })
    return Promise.resolve()
  },

  // Open/close the app-wide single-camera bypass-info dialog. Keeps deviceId on
  // close so the dialog content stays intact through the leave animation.
  openBypassInfo: function ({ commit }, { deviceId } = {}) {
    commit(SET_BYPASS_INFO, { open: true, deviceId })
  },
  closeBypassInfo: function ({ commit }) {
    commit(SET_BYPASS_INFO, { open: false })
  },

  // Open/close the app-wide single-camera bypass-create dialog. Keeps ctx on
  // close so the dialog content stays intact through the leave animation.
  openBypassCreate: function ({ commit }, ctx = {}) {
    commit(SET_BYPASS_CREATE, { open: true, ctx })
  },
  closeBypassCreate: function ({ commit }) {
    commit(SET_BYPASS_CREATE, { open: false })
  },
}

const mutations = {
  [RESET_STATE]: function (state) {
    for (let f in state) {
      Vue.set(state, f, initialState[f])
    }
  },
  [SET_BYPASS]: function (state, { deviceId, record }) {
    state.bypasses = { ...state.bypasses, [deviceId]: record }
  },
  [REMOVE_BYPASS]: function (state, { deviceId }) {
    const next = { ...state.bypasses }
    delete next[deviceId]
    state.bypasses = next
  },
  [REMOVE_BYPASSES]: function (state, { deviceIds }) {
    const next = { ...state.bypasses }
    ;(deviceIds || []).forEach(id => { delete next[id] })
    state.bypasses = next
  },
  [SET_BYPASS_CLEAR_REQUEST]: function (state, { deviceId, record }) {
    state.clearRequests = { ...state.clearRequests, [deviceId]: record }
  },
  [REMOVE_BYPASS_CLEAR_REQUEST]: function (state, { deviceId }) {
    const next = { ...state.clearRequests }
    delete next[deviceId]
    state.clearRequests = next
  },
  [REMOVE_BYPASS_CLEAR_REQUESTS]: function (state, { deviceIds }) {
    const next = { ...state.clearRequests }
    ;(deviceIds || []).forEach(id => { delete next[id] })
    state.clearRequests = next
  },
  [SET_BYPASS_NOW]: function (state, now) {
    state.now = now
    let next = null
    for (const id of Object.keys(state.bypasses)) {
      const r = state.bypasses[id]
      // Active fixed-duration bypass expired → clear (Spec cross-feature rule #6).
      if (r.status !== 'awaiting' && r.endTime != null && r.endTime <= now) {
        if (!next) next = { ...state.bypasses }
        delete next[id]
        continue
      }
      // Pending request hit the App Timeout with no local response. The server
      // resolves the timeout authoritatively (per its on_timeout); we only drop the
      // stale pending from view — MQTT / the next fetch reflects the real resolution.
      if (r.status === 'awaiting' && r.requestExpiresAt != null && r.requestExpiresAt <= now) {
        if (!next) next = { ...state.bypasses }
        delete next[id]
      }
    }
    if (next) state.bypasses = next
    // Same timeout drop for pending clear requests (server resolves authoritatively).
    let nextClear = null
    for (const id of Object.keys(state.clearRequests)) {
      const r = state.clearRequests[id]
      if (r.requestExpiresAt != null && r.requestExpiresAt <= now) {
        if (!nextClear) nextClear = { ...state.clearRequests }
        delete nextClear[id]
      }
    }
    if (nextClear) state.clearRequests = nextClear
  },
  [SET_BYPASS_SETTINGS]: function (state, { type, patch }) {
    const cur = state.settings[type] || {}
    state.settings = { ...state.settings, [type]: { ...cur, ...patch } }
  },
  [SET_BYPASS_SEEDED]: function (state, siteId) {
    state.seededSiteId = siteId
  },
  [SET_BYPASS_TICKER]: function (state, running) {
    // flag only; the actual interval lives in module scope (tickerHandle)
    state._tickerRunning = running
  },
  [SET_BYPASS_INFO]: function (state, { open, deviceId }) {
    state.infoOpen = open
    if (deviceId !== undefined) state.infoDeviceId = deviceId
  },
  [SET_BYPASS_CREATE]: function (state, { open, ctx }) {
    state.createOpen = open
    if (ctx !== undefined) state.createCtx = ctx
  },
  [SET_SECURITY_ACTION_ERROR]: function (state, error) {
    state.actionError = error || null
  },
}

// ── helpers ──────────────────────────────────────────────────────────────────

function buildRecord (deviceId, siteId, fields) {
  return {
    deviceId,
    siteId: siteId || null,
    status: 'bypassed',
    mode: 'duration',
    startTime: Date.now(),
    endTime: null,
    durationKey: null,
    durationLabel: '',
    initiator: 'me',
    initiatorName: 'You',
    reason: '',
    // Approval-request fields (operator-initiated bypass, Spec §승인 플로우)
    requestExpiresAt: null, // epoch ms the 2-min approval window closes
    requestedCustomMs: 0,
    ...fields,
  }
}

function startTicker (commit) {
  if (tickerHandle) return
  commit(SET_BYPASS_TICKER, true)
  tickerHandle = setInterval(() => {
    commit(SET_BYPASS_NOW, Date.now() + serverOffset)
  }, 1000)
}

export default {
  state,
  getters,
  actions,
  mutations,
}
